// noinspection JSUnusedGlobalSymbols

import {type ClassValue, clsx} from "clsx";
import {format, parseISO} from "date-fns";
import {twMerge} from "tailwind-merge";
import {isNumber as _isNumber} from 'lodash';

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

export function timeout(delay: number) {
  return new Promise(res => setTimeout(res, delay));
}

export function getLoginFromUrl() {
  let from = window.location.pathname;
  if (window.location.search) {
    from += window.location.search;
  }
  const params = new URLSearchParams();
  params.set("from", from);
  return "/login?" + params.toString();
}

export function toDate(dateVal?: any): Date {
  const date = toDateOrNull(dateVal);
  if (date instanceof Date) {
    return date
  }
  return new Date();
}

export function toDateOrNull(dateVal?: any): Date | null {
  if (!dateVal) {
    return null;
  }
  if (dateVal instanceof Date) {
    return dateVal;
  }
  if (typeof dateVal === "string") {
    let datePart: string;
    let timePart = "";
    if (dateVal.indexOf("T") > 0) {
      return parseISO(dateVal);
    } else {
      const parts = dateVal.split(" ");
      if (parts.length > 1) {
        datePart = parts[0];
        timePart = parts[1];
      } else {
        datePart = dateVal;
      }
    }
    let d = datePart ? datePart.split("-") : [];
    let t = timePart ? timePart.split(":") : [];
    if (d.length === 3 && t.length >= 3) {
      return new Date(parseInt(d[0]), parseInt(d[1]) - 1, parseInt(d[2]), parseInt(t[0]), parseInt(t[1]), parseInt(t[2].split(".")[0]));
    } else if (d.length === 3) {
      return new Date(parseInt(d[0]), parseInt(d[1]) - 1, parseInt(d[2]));
    }
  }
  try {
    return new Date(dateVal);
  } catch (e) {
    return null;
  }
}

export function fDateTime(dateVal?: string | Date) {
  if (!dateVal) {
    return "";
  }
  return format(toDate(dateVal), "MM/dd/yy hh:mm a");
}

export function fDate(dateVal?: string | Date) {
  if (!dateVal) {
    return "";
  }
  return format(toDate(dateVal), "MM/dd/yy");
}

export function fDateMMM(dateVal?: string | Date) {
  if (!dateVal) {
    return "";
  }
  return format(toDate(dateVal), "MMM d, yyyy");
}

export const isEmpty = (obj: any, zeroEmpty: boolean = false) => {
  if (typeof obj === "undefined") {
    return true;
  }
  if (obj === null) {
    return true;
  }
  if (typeof zeroEmpty !== "undefined" && zeroEmpty) {
    if (typeof obj === "number") return obj === 0;
    if (typeof obj === "string") return obj === "" || obj === "0";
  } else {
    if (typeof obj === "string") return obj === "";
    if (typeof obj === "number") return isNaN(obj);
  }
  if (obj instanceof Date) {
    const now = new Date();
    return obj === now;
  }
  if (typeof obj === "object") {
    if (obj.length && obj.length === 0) {
      return true;
    }
    return obj && Object.keys(obj).length === 0;
  }
  return false;
};


const usCurrencyFormatter: Intl.NumberFormat = new Intl.NumberFormat("en-US", {
  style: "currency",
  currency: "USD"
});

export function fAmt(amt: any, def = ""): string {
  if (isEmpty(amt)) {
    return def;
  }
  return usCurrencyFormatter.format(toNumber(amt));
}

const usNumFormatter: Intl.NumberFormat = new Intl.NumberFormat("en-US", {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

export function fNum(num: any, def = "") {
  if (isEmpty(num)) {
    return def;
  }
  return usNumFormatter.format(toNumber(num));
}

export function fFixedNum(val: any, decimals = 2) {
  return toNumber(val).toFixed(decimals);
}

export function isNumber(val: any): boolean {
  return !isNaN(val);
}

export function isNumeric(value: any) {
  return _isNumber(value);
}

export function toNumber(val: any): number {
  if (isNumber(val)) {
    val = parseFloat(val);
  } else if (typeof val === "string") {
    val = parseFloat(val.replace(/[^0-9.-]/g, ""));
  }
  if (isNaN(val))
    val = 0;
  return val;
}

export function roundUp(num: any, decimals = 2): number {
  num = toNumber(num);
  if (num == 0) return 0;
  decimals = isNaN(decimals) ? 2 : Math.abs(decimals);
  const roundNum = Math.pow(10, decimals);
  num = Math.round(num * roundNum) / roundNum;
  return num;
}

export function objGet<T, K extends keyof T>(obj: T, key: K, def = undefined) {
  if (typeof obj !== "object" || obj === null)
    return def;
  const parts = typeof key === "string" ? key.split(".") : [key];
  const first = parts.shift();
  if (first) {
    // @ts-expect-error temp fix
    // eslint-disable-next-line
    const val: any = obj.hasOwnProperty(first) ? obj[first] : def;
    if (parts.length > 1) {
      if (typeof val === "object" && val !== null) {
        return objGet(val, parts.join("."));
      } else {
        return def;
      }
    } else {
      return val;
    }
  }
  return def;
}

export function objHas<T, K extends keyof T>(obj: T, key: K) {
  if (typeof obj !== "object" || obj === null)
    return false;
  let first = key;
  let parts: string[] = [];
  if (typeof key === "string") {
    parts = key.split(".");
    if (parts.length > 0) {
      // @ts-expect-error temp fix
      first = parts.shift() as string;
    }
  }
  if (first) {
    if (parts.length > 1) {
      if (typeof obj[first] === "object" && obj[first] !== null) {
        return objHas(obj[first], parts.join(".") as any);
      } else {
        return typeof obj[first] !== "undefined";
      }
    } else {
      return typeof obj[first] !== "undefined";
    }
  }
  return false;
}

export function ellipsis(str?: string, maxLength = 50, ending = "...") {
  if (!str) {
    return "";
  }
  if (str.length <= maxLength) {
    return str;
  } else {
    return str.substring(0, maxLength - ending.length) + ending;
  }
}

export function uniqueId(prefix = "") {
  return prefix + Math.random().toString(36).substring(2, 11);
}

export function camelCaseKeys<T>(obj: T): T {
  if (typeof obj !== "object" || obj === null) {
    return obj;
  }

  if (Array.isArray(obj)) {
    return obj.map(camelCaseKeys) as T;
  }

  const newObj = {};
  for (const key in obj) {
    if (obj.hasOwnProperty(key)) {
      // @ts-ignore
      const camelCaseKey = key.replace(/_([a-z])/g, (match, letter) => letter.toUpperCase());
      // @ts-ignore
      newObj[camelCaseKey] = camelCaseKeys(obj[key]);
    }
  }
  return newObj as T;
}

export function nl2br(str: string) {
  return str.replace(/\r\n|\r|\n/g, "<br>");
}